// What remains of the Devices page (retired 22 Oct, docs/tasks/35): the device
// details shown as the cabinet page's "Details & log" tab, and the webhooks
// section of the Integrations page.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../context/AppContext";

const EMULATOR_HARDWARE = "emulator";

export function isEmulator(device) {
  return (device?.hardware || "").toLowerCase() === EMULATOR_HARDWARE;
}
// inline: shown as a tab of the cabinet page (docs/tasks/35), not as an overlay.
export function DeviceDetailsDrawer({ deviceId, onClose, inline = false }) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [log, setLog] = useState([]);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const [direction, setDirection] = useState("");

  const reload = async () => {
    try {
      const d = await api.getDevice(deviceId);
      setData(d);
      setDirection(d.camera_direction || "");
    } catch (e) {
      setErr(e.message || "failed");
    }
    try {
      const r = await api.getDevicePipelineLog(deviceId, { limit: 30 });
      setLog((r && r.entries) || []);
    } catch (_e) {
      /* ignore */
    }
  };

  useEffect(() => {
    if (!deviceId) return undefined;
    void reload();
    const i = window.setInterval(reload, 3000);
    return () => window.clearInterval(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  const saveDirection = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.updateDevice(deviceId, { camera_direction: direction || null });
      await reload();
    } catch (e) {
      setErr(e.message || "failed");
    } finally {
      setBusy(false);
    }
  };

  const calibrate = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.sendDeviceCommand(deviceId, "calibrate.capture", { count: 200, interval_s: 0.4 });
    } catch (e) {
      setErr(e.message || "failed");
    } finally {
      setBusy(false);
    }
  };

  const takeSnapshot = async () => {
    setBusy(true);
    setErr(null);
    setSnapshot(null);
    try {
      await api.sendDeviceCommand(deviceId, "live_stream.enable", {});
      // Wait up to 6s for a frame.
      const deadline = Date.now() + 6000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 700));
        try {
          const r = await api.getDeviceFrameUrl(deviceId);
          if (r && r.url) {
            setSnapshot(r.url);
            break;
          }
        } catch (_e) {
          /* keep polling */
        }
      }
      try {
        await api.sendDeviceCommand(deviceId, "live_stream.disable", {});
      } catch (_e) {
        /* ignore */
      }
    } catch (e) {
      setErr(e.message || "failed");
    } finally {
      setBusy(false);
    }
  };

  if (!deviceId) return null;

  return (
    <div className={inline ? "" : "fixed inset-0 z-40 flex"}>
      {!inline && <div className="flex-1 bg-slate-900/30" onClick={onClose} />}
      <aside className={inline ? "card flex flex-col" : "flex w-full max-w-xl flex-col overflow-y-auto bg-white shadow-xl"}>
        <div className={inline ? "hidden" : "flex items-start justify-between border-b border-slate-200 px-4 py-3"}>
          <div>
            <h2 className="text-lg font-semibold">{data?.name || "Device details"}</h2>
            <div className="font-mono text-xs text-slate-500">{deviceId}</div>
          </div>
          <button className="btn-secondary" onClick={onClose}>Close</button>
        </div>
        <div className="space-y-4 px-4 py-3 text-sm">
          {err ? <div className="rounded border border-rose-300 bg-rose-50 px-3 py-2 text-rose-700">{err}</div> : null}

          {data ? (
            <section>
              <h3 className="mb-1 text-xs font-semibold uppercase text-slate-500">Status</h3>
              <div className="grid grid-cols-2 gap-1 text-xs">
                <div>Location: {data.location || "—"}</div>
                <div>Hardware: {data.hardware || "—"}</div>
                <div>IP: <code>{data.ip_address || "—"}</code></div>
                <div>Camera mode: {data.camera_mode || "—"}</div>
                <div>IoT Hub: <code>{data.iot_hub_device_id || "—"}</code></div>
                <div>
                  State:{" "}
                  {data.revoked
                    ? "revoked"
                    : isEmulator(data)
                      ? "virtual (always reachable, never heartbeats)"
                      : data.online
                        ? "online"
                        : "offline"}
                </div>
                <div>Last seen: {data.last_seen_at ? new Date(data.last_seen_at * 1000).toLocaleString() : "—"}</div>
                <div>Camera direction: <strong>{data.camera_direction || "—"}</strong></div>
              </div>
            </section>
          ) : null}

          <section>
            <h3 className="mb-1 text-xs font-semibold uppercase text-slate-500">Remote console</h3>
            <p className="mb-2 text-xs text-slate-500">
              Live logs and safe commands over the device agent — works from any network,
              including behind NAT. An interactive shell needs a time-boxed grant.
            </p>
            <Link to={`/cabinets/${deviceId}?tab=console`} className="btn-secondary inline-block text-xs">
              Open console
            </Link>
          </section>

          <section>
            <h3 className="mb-1 text-xs font-semibold uppercase text-slate-500">Camera direction</h3>
            <p className="mb-2 text-xs text-slate-500">
              Configures motion + taken/returned logic. <code>static</code> disables the dynamic classifier.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <select
                className="rounded border border-slate-300 px-2 py-1 text-sm"
                value={direction}
                onChange={(e) => setDirection(e.target.value)}
              >
                <option value="">—</option>
                {CAMERA_DIRECTION_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <button className="btn-secondary text-xs" disabled={busy} onClick={saveDirection}>Save</button>
              <button className="btn-secondary text-xs" disabled={busy} onClick={takeSnapshot}>Take snapshot</button>
            </div>
            {snapshot ? (
              <img src={snapshot} alt="snapshot" className="mt-2 max-h-72 w-auto rounded border border-slate-200" />
            ) : null}
          </section>

          {data && data.desired_model ? (
            <section>
              <h3 className="mb-1 text-xs font-semibold uppercase text-slate-500">Desired model</h3>
              <div className="rounded border border-slate-200 bg-slate-50 px-2 py-1 text-xs">
                <div className="font-mono" title={data.desired_model.id}>{data.desired_model.id}</div>
                {data.desired_model.missing ? (
                  <div className="text-rose-700">missing in registry</div>
                ) : (
                  <>
                    <div>v{data.desired_model.version} · {data.desired_model.status} · {data.desired_model.class_count} classes</div>
                    <div>profiles: {(data.desired_model.target_profiles || []).join(", ") || "—"}</div>
                    {data.desired_model.metrics && data.desired_model.metrics.map50 != null ? (
                      <div>mAP@50: {Number(data.desired_model.metrics.map50).toFixed(2)}</div>
                    ) : null}
                  </>
                )}
              </div>
            </section>
          ) : null}

          {data && data.reported_model ? (
            <section>
              <h3 className="mb-1 text-xs font-semibold uppercase text-slate-500">Reported model (differs)</h3>
              <div className="rounded border border-amber-300 bg-amber-50 px-2 py-1 font-mono text-xs">{data.reported_model.id}</div>
            </section>
          ) : null}

          {data && data.latest_deployment ? (
            <section>
              <h3 className="mb-1 text-xs font-semibold uppercase text-slate-500">Latest deployment</h3>
              <div className="rounded border border-slate-200 bg-slate-50 px-2 py-1 text-xs">
                <div className="font-mono" title={data.latest_deployment.deployment_id}>
                  {(data.latest_deployment.deployment_id || "").slice(0, 8)} · {data.latest_deployment.status || "—"}
                </div>
                <div>
                  progress: {Math.round((data.latest_deployment.progress || 0) * 100)}%
                  {data.latest_deployment.created_at
                    ? ` · ${new Date(data.latest_deployment.created_at * 1000).toLocaleString()}`
                    : ""}
                </div>
              </div>
            </section>
          ) : null}

          <section>
            <h3 className="mb-1 text-xs font-semibold uppercase text-slate-500">Actions</h3>
            <div className="flex flex-wrap gap-2">
              <button
                className="rounded border border-amber-300 px-3 py-1 text-xs text-amber-700 hover:bg-amber-50"
                disabled={busy}
                onClick={calibrate}
              >
                Calibrate background (200 frames)
              </button>
            </div>
          </section>

          <section>
            <h3 className="mb-1 text-xs font-semibold uppercase text-slate-500">Pipeline log</h3>
            <div className="max-h-72 overflow-auto rounded border border-slate-200 bg-slate-50 font-mono text-[11px]">
              {log.length === 0 ? (
                <div className="p-2 text-slate-500">no entries (yet)</div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {log.slice().reverse().map((e, i) => (
                    <li key={i} className="px-2 py-1">
                      <span className="text-slate-400">
                        {e.ts ? new Date(e.ts * 1000).toLocaleTimeString() : "—"}
                      </span>{" "}
                      {/* The runtime writes {ts, stage, ...fields}; until 22 Oct this read event/detail and showed "?". */}
                      <span className="font-medium">{e.stage || e.event || "?"}</span>
                      {(() => {
                        const { ts: _ts, stage: _st, event: _ev, ...rest } = e;
                        const text = e.detail ? (typeof e.detail === "string" ? e.detail : JSON.stringify(e.detail)) : JSON.stringify(rest);
                        return text && text !== "{}" ? <span className="text-slate-600"> · {text.length > 300 ? `${text.slice(0, 300)}…` : text}</span> : null;
                      })()}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        </div>
      </aside>
    </div>
  );
}

// Every event the backend can send (shared/events.py, handbook ch. 16). An empty
// selection subscribes to all of them, as every webhook made before the filter.
const WEBHOOK_EVENTS = [
  "door.opened", "door.closed", "door.open_failed", "door.blind", "door.visible",
  "cart.settled", "session.closed", "shelf.low_stock", "shelf.counted", "cabinet.service", "cabinet.ready",
];

export function WebhooksSection() {
  const api = useApi();
  const [hooks, setHooks] = useState([]);
  const blank = { url: "", secret_ref: "", kind: "pos", description: "", events: [] };
  const [form, setForm] = useState(blank);
  const [err, setErr] = useState(null);

  const load = async () => {
    try {
      const data = await api.listWebhooks();
      // list_webhooks answers {webhooks: [...]}; until 22 Oct this read .data and always showed none.
      setHooks(Array.isArray(data) ? data : data?.webhooks || []);
    } catch (e) {
      setErr(e.message || "failed");
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    try {
      await api.createWebhook({ ...form, events: form.events.length ? form.events : null });
      setForm(blank);
      await load();
    } catch (e2) {
      setErr(e2.message || "failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this webhook?")) return;
    try {
      await api.deleteWebhook(id);
      await load();
    } catch (e) {
      setErr(e.message || "failed");
    }
  };

  return (
    <div className="card">
      <h2 className="mb-2 text-sm font-semibold">Webhooks</h2>
      <p className="mb-3 text-xs text-slate-500">
        Register your own HTTPS endpoint to receive signed events: the door (<code>door.opened</code>, <code>door.closed</code>,{" "}
        <code>door.open_failed</code>, <code>door.blind</code>, <code>door.visible</code>), the cart (<code>cart.settled</code>),{" "}
        a visit that was not a purchase (<code>session.closed</code>) the shelves (<code>shelf.low_stock</code>, <code>shelf.counted</code>) and the cabinet going into and out of service (<code>cabinet.service</code>, <code>cabinet.ready</code>).
        Pick none to receive all of them.
      </p>
      {err ? <div className="mb-2 rounded border border-rose-300 bg-rose-50 px-2 py-1 text-xs text-rose-700">{err}</div> : null}
      <form onSubmit={submit} className="mb-3 grid grid-cols-1 gap-2 md:grid-cols-2">
        <input required className="input" placeholder="https://your-domain/webhook" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
        <input required className="input" placeholder="secret reference" value={form.secret_ref} onChange={(e) => setForm({ ...form, secret_ref: e.target.value })} />
        <input className="input md:col-span-2" placeholder="description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs md:col-span-2">
          {WEBHOOK_EVENTS.map((ev) => (
            <label key={ev} className="flex items-center gap-1">
              <input type="checkbox" checked={form.events.includes(ev)}
                onChange={(e) => setForm({ ...form, events: e.target.checked ? [...form.events, ev] : form.events.filter((x) => x !== ev) })} />
              <code>{ev}</code>
            </label>
          ))}
        </div>
        <button className="btn-primary md:col-span-2" type="submit">Register webhook</button>
      </form>
      <ul className="divide-y text-sm">
        {hooks.length === 0 ? <li className="py-2 text-slate-500">No webhooks registered.</li> : null}
        {hooks.map((h) => (
          <li key={h.id} className="flex items-center justify-between gap-2 py-2">
            <div className="min-w-0">
              <div className="truncate font-mono text-xs">{h.url}</div>
              <div className="text-xs text-slate-500">{h.kind} · {h.enabled ? "enabled" : "disabled"} · secret <code>{h.secret_ref}</code></div>
              <div className="text-xs text-slate-500">{h.events && h.events.length ? h.events.join(", ") : "all events"}{h.description ? ` · ${h.description}` : ""}</div>
            </div>
            <button onClick={() => del(h.id)} className="rounded border border-rose-300 px-2 py-1 text-xs text-rose-700 hover:bg-rose-50">Delete</button>
          </li>
        ))}
      </ul>
    </div>
  );
}
